import { useEffect, useState } from 'react';
import { Copy, ExternalLink, Link as LinkIcon, Mail } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { useSendInvoiceForPayment, type SendInvoiceDelivery } from '@/hooks/useInvoices';
import type { Invoice } from '@/types/invoice';

/** Copy a pay link to the clipboard and confirm with a toast. */
export async function copyPaymentLink(url: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(url);
    toast({ title: 'Pay link copied', description: url });
  } catch {
    toast({
      title: 'Could not copy link',
      description: 'Your browser blocked clipboard access. Copy it manually instead.',
      variant: 'destructive',
    });
  }
}

interface SendForPaymentDialogProps {
  invoice: Pick<Invoice, 'id' | 'invoice_number' | 'customer_name' | 'customer_email' | 'total_amount' | 'payment_url'> | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Publish an invoice to Square for online card payment. Either has Square
 * email the customer a pay link, or just creates the link for the office to
 * copy / print.
 */
export function SendForPaymentDialog({ invoice, open, onOpenChange }: SendForPaymentDialogProps) {
  const { formatMoney } = useCompanyLocale();
  const send = useSendInvoiceForPayment();
  const hasEmail = Boolean(invoice?.customer_email);
  const [deliver, setDeliver] = useState<SendInvoiceDelivery>(hasEmail ? 'email' : 'link_only');
  const [resultUrl, setResultUrl] = useState<string | null>(null);

  // Reset per invoice / per open so a re-opened dialog starts clean.
  useEffect(() => {
    if (open) {
      setDeliver(hasEmail ? 'email' : 'link_only');
      setResultUrl(null);
    }
  }, [open, hasEmail, invoice?.id]);

  const handleSend = async () => {
    if (!invoice) return;
    try {
      const result = await send.mutateAsync({ invoiceId: invoice.id, deliver });
      if (result.payment_url) {
        setResultUrl(result.payment_url);
      } else {
        onOpenChange(false);
      }
    } catch {
      // Toast handled by the hook
    }
  };

  const options: { value: SendInvoiceDelivery; title: string; body: string; icon: typeof Mail; disabled?: boolean; hint?: string }[] = [
    {
      value: 'email',
      title: 'Email the customer a pay link',
      body: 'We email the customer a branded invoice with a secure Square link to pay by card.',
      icon: Mail,
      disabled: !hasEmail,
      hint: hasEmail ? undefined : 'Add an email address to the invoice to use this option.',
    },
    {
      value: 'link_only',
      title: 'Just create the link (copy or print it)',
      body: 'Nothing is emailed. The link is printed on the invoice and you can copy it.',
      icon: LinkIcon,
    },
  ];

  return (
    <Dialog open={open} onOpenChange={(next) => !send.isPending && onOpenChange(next)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Send for payment</DialogTitle>
          <DialogDescription>
            Create an online pay link through your Square account.
          </DialogDescription>
        </DialogHeader>

        {invoice && (
          <div className="space-y-4">
            <div className="rounded-lg border p-3 text-sm">
              <div className="flex items-baseline justify-between gap-3">
                <span className="font-medium">{invoice.invoice_number}</span>
                <span className="font-semibold tabular-nums">{formatMoney(invoice.total_amount)}</span>
              </div>
              <div className="text-muted-foreground">{invoice.customer_name}</div>
              {invoice.customer_email && (
                <div className="text-xs text-muted-foreground">{invoice.customer_email}</div>
              )}
            </div>

            {resultUrl ? (
              <div className="space-y-2">
                <p className="text-sm font-medium">Pay link</p>
                <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2">
                  <span className="min-w-0 flex-1 break-all font-mono text-xs">{resultUrl}</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="shrink-0 gap-1"
                    onClick={() => void copyPaymentLink(resultUrl)}
                  >
                    <Copy className="h-3.5 w-3.5" />
                    Copy
                  </Button>
                  <Button asChild size="sm" variant="ghost" className="shrink-0 gap-1">
                    <a href={resultUrl} target="_blank" rel="noreferrer">
                      <ExternalLink className="h-3.5 w-3.5" />
                      Open
                    </a>
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  The customer can pay by card at this link. When they do, the invoice is marked
                  paid and a job payment is recorded automatically.
                </p>
              </div>
            ) : (
              <RadioGroup
                value={deliver}
                onValueChange={(v) => setDeliver(v as SendInvoiceDelivery)}
                className="gap-2"
              >
                {options.map((opt) => {
                  const Icon = opt.icon;
                  const selected = deliver === opt.value;
                  return (
                    <label
                      key={opt.value}
                      className={cn(
                        'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition',
                        selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50',
                        opt.disabled && 'cursor-not-allowed opacity-60 hover:bg-transparent',
                      )}
                    >
                      <RadioGroupItem value={opt.value} disabled={opt.disabled} className="mt-0.5" />
                      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="min-w-0 text-sm">
                        <div className="font-medium">{opt.title}</div>
                        <div className="text-muted-foreground">{opt.body}</div>
                        {opt.hint && <div className="mt-1 text-xs text-warning">{opt.hint}</div>}
                      </div>
                    </label>
                  );
                })}
              </RadioGroup>
            )}
          </div>
        )}

        <DialogFooter>
          {resultUrl ? (
            <Button onClick={() => onOpenChange(false)}>Done</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={send.isPending}>
                Cancel
              </Button>
              <Button onClick={handleSend} disabled={!invoice || send.isPending}>
                {send.isPending
                  ? 'Sending…'
                  : deliver === 'email'
                    ? 'Send email'
                    : 'Create link'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
